import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import { GoogleMap, useLoadScript, PolygonF, MarkerF } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { aubergeine, nightMode, retro } from '../../styles/mapStyles';
import {
  BoltIcon,
  XMarkIcon,
  ChartBarIcon,
  MapIcon,
  UserGroupIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeIcon,
  EyeSlashIcon,
  CogIcon,
  PlayIcon,
  PauseIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  BeakerIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CursorArrowRaysIcon,
  SunIcon,
  MoonIcon,
  LightBulbIcon
} from '@heroicons/react/20/solid';

// Performance constants
const MAX_RECORDS = 25000;
const MAX_MAP_MARKERS = 15000;

// Default quality thresholds
const DEFAULT_THRESHOLDS = {
  compaction: {
    pass: 92.0,
    warning: 88.0,
    fail: 0.0
  },
  density: {
    pass: 145.0,
    warning: 140.0,
    fail: 0.0
  }
};

// Quality-based marker icons
const qualityMarkerIcons = {
  pass: {
    path: 'M10 20c-5.523 0-10-4.477-10-10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z',
    fillColor: '#10B981',
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: '#FFFFFF',
    scale: 0.4,
  },
  warning: {
    path: 'M10 20c-5.523 0-10-4.477-10-10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z',
    fillColor: '#F59E0B',
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: '#FFFFFF',
    scale: 0.4,
  },
  fail: {
    path: 'M10 20c-5.523 0-10-4.477-10-10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z',
    fillColor: '#EF4444',
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: '#FFFFFF',
    scale: 0.4,
  },
  unknown: {
    path: 'M10 20c-5.523 0-10-4.477-10-10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z',
    fillColor: '#6B7280',
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: '#FFFFFF',
    scale: 0.4,
  }
};

// Custom cluster renderer
const createClusterRenderer = () => {
  if (!window.google || !window.google.maps) return null;

  return {
    render: ({ count, position }) => {
      const color = count > 1000 ? "#dc2626" : count > 500 ? "#ea580c" : count > 100 ? "#d97706" : "#059669";
      const size = count > 1000 ? 80 : count > 500 ? 70 : count > 100 ? 60 : 50;
      
      return new window.google.maps.Marker({
        position,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: color,
          fillOpacity: 0.8,
          strokeWeight: 2,
          strokeColor: "#ffffff",
          scale: size / 2,
        },
        label: {
          text: count > 999 ? `${(count/1000).toFixed(1)}k` : String(count),
          color: "white",
          fontSize: count > 1000 ? "16px" : count > 100 ? "14px" : "12px",
          fontWeight: "bold",
        },
        title: `${count} measurements`,
        zIndex: 1000,
      });
    },
  };
};

export default function MeasurementHQ() {
  const { user } = useContext(AuthContext);
  
  // Core data states
  const [measurements, setMeasurements] = useState([]);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [projects, setProjects] = useState([]); // Now contains full project data with geofences
  const [projectsWithGeofences, setProjectsWithGeofences] = useState([]);
  const [operators, setOperators] = useState([]);
  const [memberIds, setMemberIds] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);
  
  // Statistics states
  const [stats, setStats] = useState({
    totalMeasurements: 0,
    totalProjects: 0,
    totalOperators: 0,
    qualityStats: { pass: 0, warning: 0, fail: 0, unknown: 0, passRate: 0 },
    recentActivity: { today: 0, thisWeek: 0, thisMonth: 0 },
    topProjects: [],
    topOperators: []
  });
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [activePanel, setActivePanel] = useState('overview'); // overview, projects, quality, operators, live-feed
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [areFiltersActive, setAreFiltersActive] = useState(false); // New state to track active filters
  const [selectedProjectFromSidebar, setSelectedProjectFromSidebar] = useState(null);
  
  // Collapsible sections
  const [sectionsCollapsed, setSectionsCollapsed] = useState({
    stats: false,
    controls: true,
    visibility: true,
    filters: true,
    projects: false
  });
  
  // Quality control states
  const [qualityAttribute, setQualityAttribute] = useState('compaction');
  const [qualityThresholds, setQualityThresholds] = useState(DEFAULT_THRESHOLDS);
  
  // Map states
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 });
  const [mapZoom, setMapZoom] = useState(10);
  const [mapStyle, setMapStyle] = useState('hybrid'); // hybrid, standard, aubergeine, nightMode, retro
  const [visibilitySettings, setVisibilitySettings] = useState({
    projects: true,
    measurements: true,
    quality: true,
    clustering: true
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    selectedProject: '',
    selectedOperator: '',
    startDate: '',
    endDate: '',
    qualityFilter: 'all', // all, pass, warning, fail
    searchTerm: ''
  });
  
  // Refs
  const mapRef = useRef(null);
  const markerClustererRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const refreshInterval = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: 'AIzaSyBBfpoVAWYZaujXG5t93mFIJKqk3cwHynw',
  });

  // Determine quality status
  const getQualityStatus = useCallback((measurement, attribute, thresholds) => {
    const value = measurement[attribute === 'compaction' ? 'Compaction' : 'Density'];
    
    if (value === null || value === undefined || isNaN(value)) {
      return 'unknown';
    }

    const limits = thresholds[attribute];
    
    if (value >= limits.pass) {
      return 'pass';
    } else if (value >= limits.warning) {
      return 'warning';
    } else {
      return 'fail';
    }
  }, []);

  // Calculate quality statistics
  const calculateQualityStats = useCallback((measurementsData, attribute, thresholds) => {
    if (!measurementsData || measurementsData.length === 0) {
      return { pass: 0, warning: 0, fail: 0, unknown: 0, total: 0, passRate: 0 };
    }

    const stats = { pass: 0, warning: 0, fail: 0, unknown: 0, total: measurementsData.length };

    measurementsData.forEach(measurement => {
      const status = getQualityStatus(measurement, attribute, thresholds);
      stats[status]++;
    });

    stats.passRate = stats.total > 0 ? Math.round((stats.pass / stats.total) * 100) : 0;
    return stats;
  }, [getQualityStatus]);

  // Calculate polygon centroid for jump to location
  const calculatePolygonCentroid = useCallback((polygon) => {
    if (!polygon || polygon.length === 0) return null;
    
    let totalLat = 0, totalLng = 0;
    polygon.forEach(point => {
      totalLat += point.lat;
      totalLng += point.lng;
    });
    
    return {
      lat: totalLat / polygon.length,
      lng: totalLng / polygon.length,
    };
  }, []);

  // Jump to location on map
  const jumpToLocation = useCallback((coord, zoom = 14) => {
    if (!isLoaded || !mapRef.current || !coord) return;
    
    setMapCenter(coord);
    setMapZoom(zoom);
    mapRef.current.panTo(coord);
    mapRef.current.setZoom(zoom);
  }, [isLoaded]);

  // Handle project click from sidebar
  const handleProjectClick = useCallback((project) => {
    // Set filter to this project
    setFilters(prev => ({ ...prev, selectedProject: project.name }));
    setSelectedProjectFromSidebar(project);
    
    // Jump to project location if it has a geofence
    if (project.polygon && project.polygon.length > 0) {
      const center = calculatePolygonCentroid(project.polygon);
      if (center) {
        jumpToLocation(center, 15);
      }
    }
  }, [calculatePolygonCentroid, jumpToLocation]);

  // Cleanup markers
  const cleanupMarkers = useCallback(() => {
    if (markerClustererRef.current) {
      markerClustererRef.current.clearMarkers();
      markerClustererRef.current = null;
    }
    
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
  }, []);

  // Create markers with clustering
  const createMarkersWithClustering = useCallback((measurementsData) => {
    if (!mapRef.current || !isLoaded || !measurementsData.length) return;
    
    if (!window.google || !window.google.maps) {
      console.warn('Google Maps API not loaded yet');
      return;
    }

    cleanupMarkers();

    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }

    const markers = measurementsData
      .filter(measurement => measurement.latitude && measurement.longitude)
      .slice(0, MAX_MAP_MARKERS)
      .map(measurement => {
        const qualityStatus = getQualityStatus(measurement, qualityAttribute, qualityThresholds);
        const markerIcon = qualityMarkerIcons[qualityStatus];
        
        const marker = new window.google.maps.Marker({
          position: { lat: measurement.latitude, lng: measurement.longitude },
          icon: markerIcon,
          title: measurement.Description1 || 'Measurement',
          map: null,
        });

        marker.addListener('click', () => {
          const attributeValue = measurement[qualityAttribute === 'compaction' ? 'Compaction' : 'Density'];
          const attributeLabel = qualityAttribute === 'compaction' ? 'Compaction' : 'Density';
          const statusColor = qualityStatus === 'pass' ? '#10B981' : qualityStatus === 'warning' ? '#F59E0B' : '#EF4444';

          const contentString = `
            <div class="p-4 bg-white rounded-lg shadow-md" style="min-width: 280px;">
              <div class="flex items-center justify-between mb-3">
                <h3 class="text-lg font-bold text-gray-800">Mission Data Point</h3>
                <div class="px-2 py-1 rounded text-xs font-bold text-white" style="background-color: ${statusColor}">
                  ${qualityStatus.toUpperCase()}
                </div>
              </div>
              <div class="space-y-2">
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="font-medium text-gray-700">Density:</span>
                    <div class="text-gray-900 font-mono">${measurement.Density?.toFixed(1) || 'N/A'} pcf</div>
                  </div>
                  <div>
                    <span class="font-medium text-gray-700">Compaction:</span>
                    <div class="text-gray-900 font-mono">${measurement.Compaction?.toFixed(1) || 'N/A'}%</div>
                  </div>
                </div>
                <div class="pt-2 border-t border-gray-200">
                  <div class="text-sm">
                    <span class="font-medium text-gray-700">Quality Status:</span>
                    <div class="font-medium" style="color: ${statusColor}">
                      ${qualityStatus.charAt(0).toUpperCase() + qualityStatus.slice(1)} 
                      (${attributeLabel}: ${attributeValue?.toFixed(1) || 'N/A'})
                    </div>
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-4 text-xs text-gray-600">
                  <div>
                    <span class="font-medium">Operator:</span>
                    <div>${measurement.operator_name || 'Unknown'}</div>
                  </div>
                  <div>
                    <span class="font-medium">Project:</span>
                    <div>${measurement.project_display_name || 'Unmapped'}</div>
                  </div>
                </div>
                <div class="text-xs text-gray-600">
                  <span class="font-medium">Timestamp:</span>
                  <div class="font-mono">${new Date(measurement.LocalTimeOfMeasurement).toLocaleString()}</div>
                </div>
              </div>
            </div>
          `;

          infoWindowRef.current.setContent(contentString);
          infoWindowRef.current.open(mapRef.current, marker);
        });

        marker.measurement = measurement;
        return marker;
      });

    markersRef.current = markers;

    if (markers.length > 0 && visibilitySettings.clustering) {
      const clusterRenderer = createClusterRenderer();
      if (clusterRenderer) {
        markerClustererRef.current = new MarkerClusterer({
          map: mapRef.current,
          markers: markers,
          renderer: clusterRenderer,
          gridSize: 60,
          maxZoom: 15,
          minimumClusterSize: 2,
          averageCenter: true,
          zoomOnClick: true,
        });
      }
    } else if (markers.length > 0) {
      // Add markers directly to map without clustering
      markers.forEach(marker => marker.setMap(mapRef.current));
    }
  }, [qualityAttribute, qualityThresholds, getQualityStatus, isLoaded, visibilitySettings.clustering]);

  // Fetch organization member IDs
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!user) return;

      try {
        let tempMemberIds = [user.id];
        
        const { data: orgMember, error: orgError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (orgError && orgError.code !== 'PGRST116') {
          console.error('Error fetching organization membership:', orgError);
          return;
        }

        if (orgMember) {
          setOrganizationId(orgMember.organization_id);
          
          const { data: orgMembers, error: orgMembersError } = await supabase
            .from('organization_members')
            .select('user_id')
            .eq('organization_id', orgMember.organization_id);

          if (orgMembersError) {
            console.error('Error fetching organization members:', orgMembersError);
            return;
          }

          tempMemberIds = orgMembers.map((member) => member.user_id);
        }

        setMemberIds(tempMemberIds);
      } catch (err) {
        console.error('Unexpected error fetching initial data:', err);
      }
    };

    fetchInitialData();
  }, [user]);

  // Fetch project geofences
  const fetchProjectGeofences = useCallback(async () => {
    if (!user || memberIds.length === 0) return;

    try {
      const { data, error } = await supabase
        .from('measurements_with_details')
        .select('project_id, project_name, project_display_name, project_geofence')
        .in('profile_id', memberIds)
        .not('project_geofence', 'is', null)
        .not('project_id', 'is', null);

      if (error) throw error;

      // Get unique projects with geofences
      const uniqueProjects = [];
      const seenProjects = new Set();

      data?.forEach(item => {
        if (!seenProjects.has(item.project_id) && item.project_geofence) {
          seenProjects.add(item.project_id);
          
          try {
            const geoJson = typeof item.project_geofence === 'string' 
              ? JSON.parse(item.project_geofence) 
              : item.project_geofence;

            if (geoJson && geoJson.coordinates && geoJson.coordinates.length > 0) {
              const coords = geoJson.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
              
              // Generate different colors for different projects
              const colors = [
                { fill: "#3B82F6", stroke: "#1D4ED8" }, // Blue
                { fill: "#10B981", stroke: "#047857" }, // Green
                { fill: "#F59E0B", stroke: "#D97706" }, // Amber
                { fill: "#EF4444", stroke: "#DC2626" }, // Red
                { fill: "#8B5CF6", stroke: "#7C3AED" }, // Purple
                { fill: "#06B6D4", stroke: "#0891B2" }, // Cyan
                { fill: "#F97316", stroke: "#EA580C" }, // Orange
                { fill: "#EC4899", stroke: "#DB2777" }, // Pink
              ];
              
              const colorIndex = uniqueProjects.length % colors.length;
              const projectColor = colors[colorIndex];

              uniqueProjects.push({
                id: item.project_id,
                name: item.project_display_name || item.project_name,
                polygon: coords,
                center: calculatePolygonCentroid(coords),
                options: {
                  fillColor: projectColor.fill,
                  fillOpacity: 0.2,
                  strokeColor: projectColor.stroke,
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                }
              });
            }
          } catch (parseError) {
            console.warn('Error parsing geofence for project:', item.project_name, parseError);
          }
        }
      });

      setProjectsWithGeofences(uniqueProjects);
    } catch (err) {
      console.error('Error fetching project geofences:', err);
      setProjectsWithGeofences([]);
    }
  }, [user, memberIds, calculatePolygonCentroid]);

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    if (!user || memberIds.length === 0) return;
  
    setLoading(true);
    try {
      let query = supabase
        .from('measurements_with_details')
        .select('*')
        .in('profile_id', memberIds)
        .limit(MAX_RECORDS);
  
      if (filters.selectedProject) {
        query = query.eq('project_display_name', filters.selectedProject);
      }
      if (filters.selectedOperator) {
        query = query.eq('operator_name', filters.selectedOperator);
      }
      if (filters.startDate) {
        query = query.gte('LocalTimeOfMeasurement', new Date(filters.startDate).toISOString());
      }
      if (filters.endDate) {
        query = query.lte('LocalTimeOfMeasurement', new Date(filters.endDate).toISOString());
      }
  
      const { data: measurementsData, error } = await query.order('LocalTimeOfMeasurement', { ascending: false });
  
      if (error) throw error;
  
      let filteredMeasurements = measurementsData || [];
  
      if (filters.qualityFilter !== 'all') {
        filteredMeasurements = filteredMeasurements.filter(measurement => {
          const qualityStatus = getQualityStatus(measurement, qualityAttribute, qualityThresholds);
          return qualityStatus === filters.qualityFilter;
        });
      }
  
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        filteredMeasurements = filteredMeasurements.filter(measurement =>
          (measurement.Description1 && measurement.Description1.toLowerCase().includes(searchLower)) ||
          (measurement.operator_name && measurement.operator_name.toLowerCase().includes(searchLower)) ||
          (measurement.project_display_name && measurement.project_display_name.toLowerCase().includes(searchLower))
        );
      }
  
      setMeasurements(filteredMeasurements);
  
      const mapMarkersData = filteredMeasurements
        .filter(m => m.latitude && m.longitude)
        .slice(0, MAX_MAP_MARKERS);
      setMapMarkers(mapMarkersData);
  
      const projectNames = Array.from(new Set(
        (measurementsData || [])
          .map(m => m.project_display_name)
          .filter(name => name && name !== 'No Project')
      )).sort();
      setProjects(projectNames);
  
      const operatorNames = Array.from(new Set(
        (measurementsData || [])
          .map(m => m.operator_name)
          .filter(name => name && name !== 'Unknown')
      )).sort();
      setOperators(operatorNames);
  
      const qualityStats = calculateQualityStats(filteredMeasurements, qualityAttribute, qualityThresholds);
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  
      const recentActivity = {
        today: filteredMeasurements.filter(m => new Date(m.LocalTimeOfMeasurement) >= today).length,
        thisWeek: filteredMeasurements.filter(m => new Date(m.LocalTimeOfMeasurement) >= weekAgo).length,
        thisMonth: filteredMeasurements.filter(m => new Date(m.LocalTimeOfMeasurement) >= monthAgo).length
      };
  
      const projectCounts = {};
      filteredMeasurements.forEach(m => {
        if (m.project_display_name) {
          projectCounts[m.project_display_name] = (projectCounts[m.project_display_name] || 0) + 1;
        }
      });
      const topProjects = Object.entries(projectCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([name, count]) => ({ name, count }));
  
      const operatorCounts = {};
      filteredMeasurements.forEach(m => {
        if (m.operator_name) {
          operatorCounts[m.operator_name] = (operatorCounts[m.operator_name] || 0) + 1;
        }
      });
      const topOperators = Object.entries(operatorCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([name, count]) => ({ name, count }));
  
      setStats({
        totalMeasurements: filteredMeasurements.length,
        totalProjects: projectNames.length,
        totalOperators: operatorNames.length,
        qualityStats,
        recentActivity,
        topProjects,
        topOperators
      });
  
      // Only update map center if no quality filter is applied and no project is selected
      if (mapMarkersData.length > 0 && filters.qualityFilter === 'all' && !filters.selectedProject) {
        const firstMeasurement = mapMarkersData[0];
        setMapCenter({ lat: firstMeasurement.latitude, lng: firstMeasurement.longitude });
      }
  
      // Update filter active state
      setAreFiltersActive(
        !!filters.selectedProject ||
        !!filters.selectedOperator ||
        !!filters.startDate ||
        !!filters.endDate ||
        filters.qualityFilter !== 'all' ||
        !!filters.searchTerm
      );
  
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, [user, memberIds, filters, qualityAttribute, qualityThresholds, getQualityStatus, calculateQualityStats]);

  // Fetch project geofences when memberIds are available
  useEffect(() => {
    if (memberIds.length > 0) {
      fetchProjectGeofences();
    }
  }, [memberIds, fetchProjectGeofences]);

  // Initial data fetch
  useEffect(() => {
    if (memberIds.length > 0) {
      fetchAllData();
    }
  }, [memberIds, fetchAllData]);

  // Auto refresh
  useEffect(() => {
    if (autoRefresh) {
      refreshInterval.current = setInterval(() => {
        fetchAllData();
        fetchProjectGeofences();
      }, 30000); // Refresh every 30 seconds
    } else {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    }

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [autoRefresh, fetchAllData, fetchProjectGeofences]);

  // Update markers when data changes
  useEffect(() => {
    if (mapMarkers.length > 0 && visibilitySettings.measurements) {
      createMarkersWithClustering(mapMarkers);
    } else {
      cleanupMarkers();
    }
  }, [mapMarkers, createMarkersWithClustering, visibilitySettings.measurements]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupMarkers();
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [cleanupMarkers]);

  // Export data
  const exportData = (format = 'csv') => {
    if (measurements.length === 0) return;

    const headers = [
      'Project', 'Operator', 'Density', 'Compaction', 'Surface Temp',
      'Quality Status', 'Latitude', 'Longitude', 'Local Time', 'Description'
    ];

    const csvData = measurements.map(m => [
      m.project_display_name || 'Unmapped',
      m.operator_name || 'Unknown',
      m.Density?.toFixed(1) || '',
      m.Compaction?.toFixed(1) || '',
      m.SurfaceTemp?.toFixed(1) || '',
      getQualityStatus(m, qualityAttribute, qualityThresholds),
      m.latitude?.toFixed(6) || '',
      m.longitude?.toFixed(6) || '',
      new Date(m.LocalTimeOfMeasurement).toLocaleString(),
      m.Description1 || ''
    ]);

    const csvContent = [headers, ...csvData].map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `measurement-hq-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Handle map style change
  const handleStyleChange = useCallback((styleName) => {
    setMapStyle(styleName);
  }, []);

  // Get map style options
  const getMapStyleOptions = useCallback(() => {
    const baseOptions = {
      fullscreenControl: false
    };

    switch (mapStyle) {
      case 'aubergeine':
        return { ...baseOptions, styles: aubergeine };
      case 'nightMode':
        return { ...baseOptions, styles: nightMode };
      case 'retro':
        return { ...baseOptions, styles: retro };
      case 'standard':
        return { ...baseOptions, mapTypeId: 'roadmap' };
      case 'hybrid':
      default:
        return { 
          ...baseOptions, 
          mapTypeId: 'hybrid',
          styles: [
            {
              featureType: 'all',
              elementType: 'labels',
              stylers: [{ visibility: 'simplified' }]
            }
          ]
        };
    }
  }, [mapStyle]);

  // Toggle section collapse
  const toggleSection = (section) => {
    setSectionsCollapsed(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Fit map bounds to show all projects
  const fitMapBounds = useCallback(() => {
    if (!isLoaded || !mapRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;

    // Add project geofences to bounds
    if (visibilitySettings.projects) {
      projectsWithGeofences.forEach(project => {
        if (project.polygon && project.polygon.length > 0) {
          project.polygon.forEach(point => {
            bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
            hasPoints = true;
          });
        }
      });
    }

    // Add measurement markers to bounds
    if (visibilitySettings.measurements) {
      mapMarkers.slice(0, 100).forEach(marker => { // Only use first 100 for performance
        bounds.extend(new window.google.maps.LatLng(marker.latitude, marker.longitude));
        hasPoints = true;
      });
    }

    if (hasPoints) {
      mapRef.current.fitBounds(bounds);
    }
  }, [isLoaded, visibilitySettings, projectsWithGeofences, mapMarkers]);

  const exitHQMode = () => {
    window.location.href = '/dashboard'; // Adjust this path as needed
  };

  if (loadError) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
        <div className="bg-white rounded-lg p-6 max-w-md">
          <div className="text-red-600 mb-4">Failed to load Google Maps: {loadError.message}</div>
          <button className="px-4 py-2 bg-gray-300 text-gray-700 rounded">Close</button>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <div className="text-lg font-semibold">Initializing HQ...</div>
          <div className="text-sm text-gray-400 mt-2">Loading measurement systems</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-gray-800 flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <BoltIcon className="h-8 w-8 text-yellow-400" />
              <h1 className="text-2xl font-bold text-white">Measurement HQ</h1>
              
            </div>
            
            
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1 bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => handleStyleChange('hybrid')}
                className={`p-2 rounded-md transition-colors ${
                  mapStyle === 'hybrid' ? 'bg-yellow-600 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
                title="Hybrid View"
              >
                <MapIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleStyleChange('standard')}
                className={`p-2 rounded-md transition-colors ${
                  mapStyle === 'standard' ? 'bg-yellow-600 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
                title="Standard View"
              >
                <SunIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleStyleChange('nightMode')}
                className={`p-2 rounded-md transition-colors ${
                  mapStyle === 'nightMode' ? 'bg-yellow-600 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
                title="Night Mode"
              >
                <MoonIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleStyleChange('aubergeine')}
                className={`p-2 rounded-md transition-colors ${
                  mapStyle === 'aubergeine' ? 'bg-yellow-600 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
                title="Aubergeine Mode"
              >
                <BoltIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleStyleChange('retro')}
                className={`p-2 rounded-md transition-colors ${
                  mapStyle === 'retro' ? 'bg-yellow-600 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
                title="Retro Mode"
              >
                <LightBulbIcon className="h-4 w-4" />
              </button>
            </div>
            
            <button
              onClick={fitMapBounds}
              className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
            >
              <MapIcon className="h-4 w-4 mr-1 inline" />
              Fit Bounds
            </button>
            
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                autoRefresh ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-200'
              }`}
            >
              {autoRefresh ? <PlayIcon className="h-4 w-4 mr-1 inline" /> : <PauseIcon className="h-4 w-4 mr-1 inline" />}
              Auto Refresh
            </button>
            
            <button
              onClick={() => exportData('csv')}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              disabled={measurements.length === 0}
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-1 inline" />
              Export Data
            </button>
            
            <button
              onClick={exitHQMode}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Controls & Stats */}
        <div className="w-80 bg-gray-900 border-r border-gray-700 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          {/* Mission Stats */}
          <div className="border-b border-gray-700">
            <button
              onClick={() => toggleSection('stats')}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-800 transition-colors"
            >
              <h3 className="text-md font-semibold text-white">Job Stats</h3>
              <div className={`transform transition-transform ${sectionsCollapsed.stats ? '' : 'rotate-180'}`}>
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              </div>
            </button>
            
            {!sectionsCollapsed.stats && (
              <div className="px-4 pb-4">
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="text-2xl font-bold text-blue-400">{stats.totalMeasurements.toLocaleString()}</div>
                    <div className="text-xs text-gray-300">Total Measurements</div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="text-2xl font-bold text-green-400">{stats.totalProjects}</div>
                    <div className="text-xs text-gray-300">Active Projects</div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="text-2xl font-bold text-yellow-400">{stats.totalOperators}</div>
                    <div className="text-xs text-gray-300">Operators</div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="text-2xl font-bold text-purple-400">{stats.qualityStats.passRate}%</div>
                    <div className="text-xs text-gray-300">Pass Rate</div>
                  </div>
                </div>

                {/* Quality Breakdown */}
                <div className="bg-gray-800 rounded-lg p-3 mb-3">
                  <div className="text-sm font-medium text-white mb-2">Quality Control</div>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div className="text-center">
                      <div className="font-bold text-green-400">{stats.qualityStats.pass}</div>
                      <div className="text-gray-300">Pass</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-yellow-400">{stats.qualityStats.warning}</div>
                      <div className="text-gray-300">Warning</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-red-400">{stats.qualityStats.fail}</div>
                      <div className="text-gray-300">Fail</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-gray-400">{stats.qualityStats.unknown}</div>
                      <div className="text-gray-300">Unknown</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Active Projects */}
          <div className="border-b border-gray-700">
            <button
              onClick={() => toggleSection('projects')}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-800 transition-colors"
            >
              <h3 className="text-md font-semibold text-white">Active Projects</h3>
              <div className={`transform transition-transform ${sectionsCollapsed.projects ? '' : 'rotate-180'}`}>
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              </div>
            </button>
            
            {!sectionsCollapsed.projects && (
              <div className="px-4 pb-4">
                <div className="text-xs text-gray-400 mb-3">
                  Click projects to jump to location and filter data
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {projectsWithGeofences.map((project, idx) => (
                    <div 
                      key={project.id}
                      onClick={() => handleProjectClick(project)}
                      className={`cursor-pointer p-2 rounded-lg transition-all hover:bg-gray-600 ${
                        filters.selectedProject === project.name ? 'bg-yellow-600 text-white' : 'bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-3 h-3 rounded-full border border-white"
                            style={{ backgroundColor: project.options.fillColor }}
                          ></div>
                          <span className="text-sm font-medium text-white truncate">
                            {project.name}
                          </span>
                        </div>
                        <CursorArrowRaysIcon className="h-4 w-4 text-gray-400" />
                      </div>
                      {filters.selectedProject === project.name && (
                        <div className="text-xs text-yellow-200 mt-1">
                          📍 Active Filter
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {projectsWithGeofences.length === 0 && (
                    <div className="text-center py-4 text-gray-400">
                      <BuildingOfficeIcon className="h-8 w-8 mx-auto mb-2" />
                      <div className="text-sm">No project geofences found</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Quality Controls */}
          <div className="border-b border-gray-700">
            <button
              onClick={() => toggleSection('controls')}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-800 transition-colors"
            >
              <h3 className="text-md font-semibold text-white">Quality Controls</h3>
              <div className={`transform transition-transform ${sectionsCollapsed.controls ? '' : 'rotate-180'}`}>
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              </div>
            </button>
            
            {!sectionsCollapsed.controls && (
              <div className="px-4 pb-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Quality Attribute</label>
                    <select
                      value={qualityAttribute}
                      onChange={(e) => setQualityAttribute(e.target.value)}
                      className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
                    >
                      <option value="compaction">Compaction (%)</option>
                      <option value="density">Density (pcf)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Pass Threshold</label>
                    <input
                      type="number"
                      step="0.1"
                      value={qualityThresholds[qualityAttribute].pass}
                      onChange={(e) => setQualityThresholds(prev => ({
                        ...prev,
                        [qualityAttribute]: {
                          ...prev[qualityAttribute],
                          pass: parseFloat(e.target.value) || 0
                        }
                      }))}
                      className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Warning Threshold</label>
                    <input
                      type="number"
                      step="0.1"
                      value={qualityThresholds[qualityAttribute].warning}
                      onChange={(e) => setQualityThresholds(prev => ({
                        ...prev,
                        [qualityAttribute]: {
                          ...prev[qualityAttribute],
                          warning: parseFloat(e.target.value) || 0
                        }
                      }))}
                      className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="border-b border-gray-700">
  <button
    onClick={() => toggleSection('filters')}
    className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-800 transition-colors"
  >
    <h3 className="text-md font-semibold text-white">Data Filters</h3>
    <div className={`transform transition-transform ${sectionsCollapsed.filters ? '' : 'rotate-180'}`}>
      <ChevronDownIcon className="w-5 h-5 text-gray-400" />
    </div>
  </button>
  {!sectionsCollapsed.filters && (
    <div className="px-4 pb-4">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Search</label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={filters.searchTerm}
              onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
              placeholder="Search measurements..."
              className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm pl-8"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Operator</label>
          <select
            value={filters.selectedOperator}
            onChange={(e) => setFilters(prev => ({ ...prev, selectedOperator: e.target.value }))}
            className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
          >
            <option value="">All Operators</option>
            {operators.map(operator => (
              <option key={operator} value={operator}>{operator}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Quality Filter</label>
          <select
            value={filters.qualityFilter}
            onChange={(e) => setFilters(prev => ({ ...prev, qualityFilter: e.target.value }))}
            className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
          >
            <option value="all">All Quality Levels</option>
            <option value="pass">Pass Only</option>
            <option value="warning">Warning Only</option>
            <option value="fail">Fail Only</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
            />
          </div>
        </div>
        <button
          onClick={() => {
            setFilters({
              selectedProject: '',
              selectedOperator: '',
              startDate: '',
              endDate: '',
              qualityFilter: 'all',
              searchTerm: ''
            });
            setSelectedProjectFromSidebar(null);
          }}
          className="w-full px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-500"
        >
          Clear All Filters
        </button>
      </div>
    </div>
  )}
</div>

          {/* Map Visibility */}
          <div className="border-b border-gray-700">
            <button
              onClick={() => toggleSection('visibility')}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-800 transition-colors"
            >
              <h3 className="text-md font-semibold text-white">Map Controls</h3>
              <div className={`transform transition-transform ${sectionsCollapsed.visibility ? '' : 'rotate-180'}`}>
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              </div>
            </button>
            
            {!sectionsCollapsed.visibility && (
              <div className="px-4 pb-4">
                <div className="space-y-2">
                  {Object.entries(visibilitySettings).map(([key, value]) => (
                    <label key={key} className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setVisibilitySettings(prev => ({ ...prev, [key]: e.target.checked }))}
                        className="rounded border-gray-600 bg-gray-800 text-yellow-600"
                      />
                      <span className="text-gray-300 capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                      {value ? <EyeIcon className="h-4 w-4 text-green-400" /> : <EyeSlashIcon className="h-4 w-4 text-gray-500" />}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          </div>
        </div>

        {/* Main Map Area */}
        <div className="flex-1 relative">
          <GoogleMap
            center={mapCenter}
            zoom={mapZoom}
            mapContainerStyle={{ width: '100%', height: '100%' }}
            options={getMapStyleOptions()}
            onLoad={(map) => {
              mapRef.current = map;
            }}
            onUnmount={() => {
              mapRef.current = null;
            }}
          >
            {/* Project Geofences */}
            {visibilitySettings.projects && projectsWithGeofences.map(project => (
              <React.Fragment key={`project_${project.id}`}>
                <PolygonF
                  path={project.polygon}
                  options={{
                    ...project.options,
                    fillOpacity: filters.selectedProject === project.name ? 0.4 : 0.2,
                    strokeWeight: filters.selectedProject === project.name ? 3 : 2,
                    zIndex: filters.selectedProject === project.name ? 1000 : 1
                  }}
                  onClick={() => handleProjectClick(project)}
                />
              </React.Fragment>
            ))}
          </GoogleMap>

          {/* Map Info Overlay */}
          <div className="absolute top-4 right-4 bg-gray-900 bg-opacity-80 text-white p-4 rounded-lg text-sm space-y-2">
  <div className="font-bold mb-2">Job Status</div>
  <div className="flex items-center space-x-2">
    <div className="w-3 h-3 bg-green-500 rounded"></div>
    <span>Pass ({stats.qualityStats.pass})</span>
  </div>
  <div className="flex items-center space-x-2">
    <div className="w-3 h-3 bg-yellow-500 rounded"></div>
    <span>Warning ({stats.qualityStats.warning})</span>
  </div>
  <div className="flex items-center space-x-2">
    <div className="w-3 h-3 bg-red-500 rounded"></div>
    <span>Fail ({stats.qualityStats.fail})</span>
  </div>
  <div className="flex items-center space-x-2">
    <div className="w-3 h-3 bg-gray-500 rounded"></div>
    <span>Unknown ({stats.qualityStats.unknown})</span>
  </div>
  <div className="border-t border-gray-600 pt-2 mt-2">
    <div className="text-xs text-gray-300">
      {mapMarkers.length.toLocaleString()} markers displayed
    </div>
    <div className="text-xs text-blue-300">
      {projectsWithGeofences.length} project geofences
    </div>
    <div className="text-xs text-purple-300">
      Map Style: {mapStyle.charAt(0).toUpperCase() + mapStyle.slice(1)}
    </div>
    {mapMarkers.length >= MAX_MAP_MARKERS && (
      <div className="text-xs text-orange-400">
        Limited to {MAX_MAP_MARKERS.toLocaleString()} for performance
      </div>
    )}
  </div>
  {/* Clear Filters Button */}
  {areFiltersActive && (
    <div className="mt-4">
      <button
        onClick={() => {
          setFilters({
            selectedProject: '',
            selectedOperator: '',
            startDate: '',
            endDate: '',
            qualityFilter: 'all',
            searchTerm: ''
          });
          setSelectedProjectFromSidebar(null);
        }}
        className="w-full px-3 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-500 flex items-center justify-center"
        disabled={loading}
        title="Reset all filters to default"
      >
        <FunnelIcon className="h-4 w-4 mr-2" />
        Clear All Filters
        {loading && <div className="ml-2 w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin"></div>}
      </button>
    </div>
  )}
</div>

          {/* Selected Project Info */}
          {filters.selectedProject && (
            <div className="absolute bottom-4 left-4 bg-yellow-600 text-white p-3 rounded-lg text-sm max-w-sm">
              <div className="flex items-center space-x-2">
                <BuildingOfficeIcon className="h-5 w-5" />
                <div>
                  <div className="font-semibold">Active Project Filter</div>
                  <div className="text-xs text-yellow-200">{filters.selectedProject}</div>
                </div>
              </div>
            </div>
          )}

          {/* Performance Warning */}
          {mapMarkers.length >= MAX_MAP_MARKERS && (
            <div className="absolute bottom-4 right-4 bg-orange-600 text-white p-3 rounded-lg text-sm max-w-sm">
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-5 w-5" />
                <div>
                  <div className="font-semibold">Performance Mode Active</div>
                  <div className="text-xs">Showing {MAX_MAP_MARKERS.toLocaleString()} of {stats.totalMeasurements.toLocaleString()} measurements</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Status Bar */}
      <div className="bg-gray-900 border-t border-gray-700 px-6 py-2">
        <div className="flex items-center justify-between text-sm text-gray-300">
          <div className="flex items-center space-x-4">
            <span>HQ Active</span>
            {loading && (
              <span className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                <span>Analyzing data...</span>
              </span>
            )}
            {!loading && (
              <span className="flex items-center space-x-1">
                <CheckCircleIcon className="w-4 h-4 text-green-400" />
                <span>All systems operational</span>
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-4">
            <span>Coordinates: {mapCenter.lat.toFixed(6)}, {mapCenter.lng.toFixed(6)}</span>
            <span>Quality: {stats.qualityStats.passRate}% Pass</span>
            <span>Projects: {projectsWithGeofences.length}</span>
            <button
              onClick={exitHQMode}
              className="px-4 py-1 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Exit HQ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};